import { AppSidebar } from "@/components/app-sidebar";
import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRecentBlocks } from "@/hooks/useDNSAnalytics";
import { formatDistanceToNow } from "date-fns";
import { Shield, Chrome, Server, ActivityIcon } from "lucide-react";

export default function Activity() {
  const { data: blocks = [], isLoading } = useRecentBlocks(100);

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--header-height": "3rem",
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-6 p-6">
            <div className="flex items-center gap-3">
              <ActivityIcon className="size-6 text-primary" />
              <div>
                <h1 className="text-2xl font-semibold">Activity</h1>
                <p className="text-sm text-muted-foreground">
                  Blocked DNS queries from your organization
                </p>
              </div>
            </div>

            <ChartAreaInteractive />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="size-4 text-green-500" />
                  Recent Blocked Queries
                  {!isLoading && (
                    <Badge variant="secondary" className="ml-auto">
                      {blocks.length} entries
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-muted-foreground">
                    Loading activity...
                  </div>
                ) : blocks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                    <Shield className="size-8 opacity-30" />
                    <p>No blocked queries yet.</p>
                    <p className="text-xs">
                      Blocked domains will appear here once your DNS server is
                      active.
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Domain</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blocks.map((block) => (
                        <TableRow key={block.id}>
                          <TableCell className="font-mono text-sm">
                            {block.domain}
                          </TableCell>
                          <TableCell>
                            {block.blocked_category ? (
                              <Badge variant="outline" className="text-xs">
                                {block.blocked_category}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                —
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {block.source === "extension" ? (
                              <span className="flex items-center gap-1 text-xs text-blue-500">
                                <Chrome className="size-3" /> Extension
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-purple-500">
                                <Server className="size-3" /> DNS
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(block.timestamp), {
                              addSuffix: true,
                            })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
